import { HashRouter, useRoutes, type RouteObject } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthGuard } from './AuthGuard';
import { useUserStore } from '@/stores/userStore.ts';
import type { RequireKeys } from '@/types/utils.ts';
import type { RouteWithFullPath } from './path';
import { getRolePermissionInfo } from '@/api/modules/auth/token';
import { getHasPermissionRoutes } from './getHasPermissionRoutes';
import axios from 'axios';

/**
 * 路由配置接口
 */
export interface RouteConfig {
  /**
   * 路由权限码，用于判断是否有权限访问，
   * 如果没有权限码，视为非侧边栏导航跳转的路由，对于没有权限码的路由，不会在侧边栏导航中显示，但是可以通过直接访问路径来跳转
   */
  permCode?: string;
  path: string;
  element?: React.ReactNode;
  children?: RouteConfig[];
  meta?: {
    title: string;
    icon?: React.ComponentType<{ className?: string }>;
    requireAuth?: boolean;
    /**
     * 侧边栏包装器，用于类似添加Popover的场景
     * @example
     * import MessagePreview from '@/layouts/AppSidebar/components/MessagePreview.tsx';
     * meta: {
     *   title: '消息中心',
     *   icon: MessageCircleMore,
     *   asideBarWrapper: MessagePreview,
     * },
     */
    asideBarWrapper?: React.ComponentType<{ children: React.ReactNode }>;
  };
}

export type HasMetaRouteConfig = RequireKeys<RouteConfig, 'meta'>;
export type HasMetaRouteWithFullPath = RequireKeys<RouteWithFullPath, 'meta'>;

/**
 * 路由组件 - 使用 useRoutes 渲染路由配置
 */
function RouteElement() {
  const { userInfo, token, setUserInfo } = useUserStore();
  const element = useRoutes(
    getHasPermissionRoutes(userInfo?.permissionCodes || []) as RouteObject[]
  );

  // 组件挂载时获取最新权限码
  useEffect(() => {
    void (async () => {
      if (token) {
        const res = await getRolePermissionInfo();
        if (userInfo && res.data?.permissionCodes) {
          const { permissionCodes, ...roleInfo } = res.data;
          setUserInfo({
            ...userInfo,
            roleInfo,
            permissionCodes,
          });
        }
      }
    })().catch((error) => {
      if (!axios.isCancel(error)) console.error('刷新用户权限失败:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，仅在组件挂载时执行

  return element;
}

/**
 * 应用路由根组件 - 使用 HashRouter
 */
export default function AppRouter() {
  return (
    <HashRouter>
      <AuthGuard>
        <RouteElement />
      </AuthGuard>
    </HashRouter>
  );
}
